import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { integrationsAPI, maxAPI } from '../services/api';
import { Plus, Trash2, Power, PowerOff, Settings, MessageCircle, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const PROVIDER_ICONS = {
  'beeline_a2p': <img src="/icons/a2p.svg" alt="Beeline A2P" style={{ width: 32, height: 32, objectFit: 'contain' }} />,
  'unisender': <img src="/icons/unisender.svg" alt="Unisender" style={{ width: 32, height: 32, objectFit: 'contain' }} />,
};


function EditModal({ integration, providers, onClose, onSaved }) {
  const provider = providers.find(p => p.provider === integration.provider);
  const [form, setForm] = useState({});
  const [costPerMessage, setCostPerMessage] = useState(integration.cost_per_message || 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {

    const initial = {};
    if (provider) {
      provider.fields.forEach(f => {
        const masked = integration.credentials_masked[f.key] || '';

        initial[f.key] = masked.includes('•') ? '' : masked;
      });
    }
    setForm(initial);
  }, [integration, provider]);

  if (!provider) return null;

  const handleSave = async () => {
    setLoading(true);
    try {

      const creds = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== undefined) creds[k] = v;
      });
      const updateData = { cost_per_message: parseFloat(costPerMessage) || 0 };
      if (Object.keys(creds).length > 0) {
        updateData.credentials = creds;
      }
      await integrationsAPI.update(integration.id, updateData);
      toast.success('Настройки обновлены');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{PROVIDER_ICONS[integration.provider]} Настройки — {integration.display_name}</h2>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          Оставьте поле пустым чтобы сохранить текущее значение
        </p>
        {provider.fields.map(f => (
          <div className="form-group" key={f.key}>
            <label className="form-label">{f.label}</label>
            {f.type === 'checkbox' ? (
              <label className="form-check">
                <input type="checkbox" checked={!!form[f.key]}
                  onChange={e => setForm({ ...form, [f.key]: e.target.checked })} />
                {f.label}
              </label>
            ) : (
              <input className="form-input"
                type={f.type === 'password' ? 'password' : f.type === 'number' ? 'number' : 'text'}
                placeholder={
                  f.type === 'password'
                    ? (integration.credentials_masked[f.key] || 'Введите новое значение')
                    : (integration.credentials_masked[f.key] || f.label)
                }
                value={form[f.key] || ''}
                onChange={e => setForm({ ...form, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })} />
            )}
          </div>
        ))}
        <div className="form-group">
          <label className="form-label">Стоимость 1 сообщения (₽)</label>
          <input className="form-input" type="number" min="0" step="0.01"
            value={costPerMessage} onChange={e => setCostPerMessage(e.target.value)}
            placeholder="0" />
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Для расчёта ROI цепочек и рассылок</div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IntegrationsPage() {
  const [providers, setProviders] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [editIntegration, setEditIntegration] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [maxConns, setMaxConns] = useState([]);
  // 'loading' | 'ready' (доступен) | 'off' (404, фича выключена) | 'error' (403/5xx/сеть)
  const [maxState, setMaxState] = useState('loading');
  const navigate = useNavigate();

  const loadMax = () => {
    setMaxState('loading');
    maxAPI.listConnections()
      .then(r => {
        // контракт backend — голый массив; .items на случай будущей обёртки
        const data = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setMaxConns(data); setMaxState('ready');
      })
      .catch(e => {
        const s = e.response?.status;
        setMaxConns([]);
        // 404 = фича выключена (маршрутов нет) -> «Скоро».
        // 401 обрабатывает глобальный интерсептор (redirect на /login).
        // 403/5xx/сеть -> ошибка загрузки (НЕ «Скоро», НЕ «не подключено»), с повтором.
        if (s === 404) setMaxState('off');
        else setMaxState('error');
      });
  };

  const load = () => {
    Promise.all([integrationsAPI.providers(), integrationsAPI.list()])
      .then(([pRes, iRes]) => { setProviders(pRes.data); setIntegrations(iRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadMax();
  };

  // active/disconnected различаем по статусу — временно неактивное НЕ скрываем
  const MAX_STATUS = {
    active: ['badge-success', 'Активна'],
    connecting: ['badge-warning', 'Подключение'],
    disconnected: ['badge-danger', 'Отключена'],
    error: ['badge-danger', 'Ошибка'],
  };

  useEffect(() => { load(); }, []);

  const handleSelectProvider = (p) => {
    setSelectedProvider(p);
    const defaults = {};
    p.fields.forEach(f => { if (f.default !== undefined) defaults[f.key] = f.default; });
    setForm(defaults);
  };

  const handleCreate = async () => {
    if (!selectedProvider) return;
    const missing = selectedProvider.fields.filter(f => f.required && !form[f.key]);
    if (missing.length) { toast.error(`Заполните: ${missing.map(f => f.label).join(', ')}`); return; }
    try {
      await integrationsAPI.create({ provider: selectedProvider.provider, credentials: form });
      toast.success('Интеграция подключена');
      setShowAdd(false); setSelectedProvider(null); setForm({});
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const handleToggle = async (integration) => {
    try {
      await integrationsAPI.update(integration.id, { is_active: !integration.is_active });
      toast.success(integration.is_active ? 'Отключено' : 'Включено');
      load();
    } catch (err) { toast.error('Ошибка'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить интеграцию?')) return;
    try { await integrationsAPI.delete(id); toast.success('Удалено'); load(); }
    catch (err) { toast.error('Ошибка'); }
  };

  const connectedProviders = new Set(integrations.map(i => i.provider));

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка...</div>;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Интеграции</h1>
        <p className="page-subtitle">Подключение каналов рассылок</p>
      </div>

      {(integrations.length > 0 || maxConns.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {integrations.map(i => (
            <div key={i.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' }}>
              <span style={{ fontSize: 24 }}>{PROVIDER_ICONS[i.provider]}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{i.display_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {Object.entries(i.credentials_masked).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                </div>
              </div>
              <span className={`badge ${i.is_active ? 'badge-success' : 'badge-danger'}`}>
                {i.is_active ? 'Активна' : 'Отключена'}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditIntegration(i)} title="Настройки">
                <Settings size={16} />
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => handleToggle(i)}
                title={i.is_active ? 'Отключить' : 'Включить'}>
                {i.is_active ? <PowerOff size={16} /> : <Power size={16} />}
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(i.id)}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {maxConns.map(c => {
            const [cls, label] = MAX_STATUS[c.status] || ['badge-muted', c.status];
            return (
              <div key={c.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' }}>
                <MessageCircle size={24} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {c.bot_username || c.title || 'MAX Мессенджер'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    MAX · {c.bot_user_id ? `бот ${c.bot_user_id}` : 'Existing Bot'}
                  </div>
                </div>
                <span className={`badge ${cls}`}>{label}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/max')} title="Настройки">
                  <Settings size={16} />
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/max')} title="Подписчики и аналитика">
                  <Users size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

 
      {!showAdd && (
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <Plus size={16} /> Подключить интеграцию
        </button>
      )}


      {showAdd && !selectedProvider && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>
            Выберите провайдера
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {providers.map(p => {
              const connected = connectedProviders.has(p.provider);
              return (
                <div key={p.provider}
                  onClick={() => !connected && handleSelectProvider(p)}
                  style={{
                    padding: 16, borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                    background: connected ? 'var(--bg-secondary)' : 'var(--bg-card)',
                    cursor: connected ? 'default' : 'pointer', opacity: connected ? 0.5 : 1,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => !connected && (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{PROVIDER_ICONS[p.provider]}</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{p.display}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {p.channel.toUpperCase()} {connected ? '· подключено' : ''}
                  </div>
                </div>
              );
            })}
            {/* MAX — отдельный коннектор; мастер на /max. off -> «Скоро»; error -> повтор; ready -> клик */}
            {(() => {
              const ready = maxState === 'ready';
              const off = maxState === 'off';
              const err = maxState === 'error';
              const interactive = ready || err;   // ready -> /max, err -> повтор запроса
              const subtitle = ready ? (maxConns.length ? 'MAX · подключено' : 'MAX · мастер подключения')
                : off ? 'MAX · Скоро'
                : err ? 'MAX · ошибка загрузки — нажмите, чтобы повторить'
                : 'MAX · загрузка…';
              return (
                <div key="max"
                  onClick={() => { if (ready) navigate('/max'); else if (err) loadMax(); }}
                  style={{
                    padding: 16, borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                    background: ready ? 'var(--bg-card)' : 'var(--bg-secondary)',
                    cursor: interactive ? 'pointer' : 'default', opacity: off ? 0.6 : 1,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => interactive && (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}><MessageCircle size={28} /></div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>MAX Мессенджер</div>
                  <div style={{ fontSize: 12, color: err ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>
                    {subtitle}
                  </div>
                </div>
              );
            })()}
          </div>
          <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={() => setShowAdd(false)}>Отмена</button>
        </div>
      )}

   
      {showAdd && selectedProvider && (
        <div className="card" style={{ marginTop: 16, maxWidth: 480 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>
            {PROVIDER_ICONS[selectedProvider.provider]} {selectedProvider.display}
          </h3>
          {selectedProvider.fields.map(f => (
            <div className="form-group" key={f.key}>
              <label className="form-label">{f.label} {f.required && '*'}</label>
              {f.type === 'checkbox' ? (
                <label className="form-check">
                  <input type="checkbox" checked={!!form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.checked })} />
                  {f.label}
                </label>
              ) : (
                <input className="form-input"
                  type={f.type === 'password' ? 'password' : f.type === 'number' ? 'number' : 'text'}
                  placeholder={f.label}
                  value={form[f.key] || ''}
                  onChange={e => setForm({ ...form, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })} />
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={handleCreate}>Подключить</button>
            <button className="btn btn-ghost" onClick={() => { setSelectedProvider(null); setShowAdd(false); }}>Отмена</button>
          </div>
        </div>
      )}

      {integrations.length === 0 && !showAdd && (
        <p style={{ color: 'var(--text-muted)', marginTop: 20 }}>
          Интеграции не подключены. Добавьте провайдера для отправки рассылок.
        </p>
      )}


      {editIntegration && (
        <EditModal
          integration={editIntegration}
          providers={providers}
          onClose={() => setEditIntegration(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}